import { Pipe, PipeTransform } from '@angular/core';
import { DateFormatType, DateUtils, ObjectUtils } from '@shared/core';
import {
  ITableColumnCellConfigType,
  ITableColumnDateCell,
  ITableColumnTextCell,
  UITableColumnCellType,
} from '../models';
import { LocalizationPipe } from '@shared/modules/localization';

@Pipe({
  name: 'propertyValue',
  pure: false,
  standalone: true,
})
export class TablePropertyValuePipe implements PipeTransform {
  constructor(private localizationPipe: LocalizationPipe) {}

  transform(
    value: NonNullable<unknown> | undefined,
    columnConfig?: ITableColumnCellConfigType | string,
  ): any {
    if (!columnConfig) {
      return value;
    }

    const propertyValue = this.resolvePropertyValue(value, columnConfig);

    if (this.isDateColumn(columnConfig)) {
      return DateUtils.transform(propertyValue, columnConfig.format);
    }

    if (this.isTranslatable(columnConfig)) {
      return this.localizationPipe.transform(propertyValue);
    }

    if (typeof columnConfig !== 'string' && !!columnConfig.formatter) {
      const formattedValue = columnConfig.formatter(propertyValue);

      if (typeof formattedValue === 'string' && this.isI18nKey(formattedValue)) {
        return this.localizationPipe.transform(formattedValue);
      }

      return formattedValue;
    }

    return propertyValue;
  }

  private resolvePropertyValue(
    value: NonNullable<unknown> | undefined,
    columnConfig: ITableColumnCellConfigType | string,
  ): any {
    const fieldName = typeof columnConfig === 'string' ? columnConfig : columnConfig?.fieldName;
    return ObjectUtils.getPropertyValue(value, fieldName);
  }

  private isDateColumn(
    columnConfig: ITableColumnCellConfigType | string,
  ): columnConfig is ITableColumnDateCell {
    return typeof columnConfig !== 'string' && columnConfig?.type === UITableColumnCellType.Date;
  }

  private isTranslatable(
    columnConfig: ITableColumnCellConfigType | string,
  ): columnConfig is ITableColumnTextCell {
    return (
      typeof columnConfig !== 'string' &&
      columnConfig?.type === UITableColumnCellType.Text &&
      !!columnConfig?.isTranslatable
    );
  }

  private isI18nKey(value: string): boolean {
    return /^[A-Z_]+$/.test(value);
  }
}
